import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  NOTE_ENTITY_TYPES,
  NOTE_VISIBILITIES,
  createCoreNote,
  fetchNoteStats,
  listCoreNotes,
  noteEntityMeta,
  noteVisibilityMeta,
  softDeleteCoreNote,
  toggleNotePin,
  updateCoreNote,
  type CoreNote,
} from '@/lib/coreNotes';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface NForm { id: number | null; entityType: string; entityId: string; title: string; body: string; visibility: string; }
const EMPTY: NForm = { id: null, entityType: '', entityId: '', title: '', body: '', visibility: 'internal' };

export default function NotesTab({ org }: Props) {
  const { user } = useAuth();
  const [items, setItems] = useState<CoreNote[]>([]);
  const [stats, setStats] = useState<{ total: number; privateCount: number; pinned: number }>({ total: 0, privateCount: 0, pinned: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([listCoreNotes(org.id), fetchNoteStats(org.id)]);
      setItems(list);
      setStats(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (entityFilter === 'all' ? items : items.filter((n) => n.entity_type === entityFilter)),
    [items, entityFilter],
  );

  const openCreate = () => { setForm({ ...EMPTY, entityType: entityFilter === 'all' ? '' : entityFilter }); setOpen(true); };
  const openEdit = (n: CoreNote) => {
    setForm({ id: n.id, entityType: n.entity_type || '', entityId: n.entity_id || '', title: n.title || '', body: n.body || '', visibility: n.visibility || 'internal' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.body.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreNote(form.id, { title: form.title.trim(), body: form.body.trim(), visibility: form.visibility });
      } else {
        await createCoreNote({
          tenantId: org.tenant_id,
          organizationId: org.id,
          entityType: form.entityType || null,
          entityId: form.entityId.trim() || null,
          title: form.title.trim(),
          body: form.body.trim(),
          visibility: form.visibility,
          authorUserId: user?.id ?? null,
          authorName: user?.name || 'Vous',
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const pin = async (n: CoreNote) => {
    setBusyId(n.id);
    try { await toggleNotePin(n.id, !n.pinned); load(); } finally { setBusyId(null); }
  };

  const remove = async (n: CoreNote) => {
    setBusyId(n.id);
    try { await softDeleteCoreNote(n.id, user?.id ?? null); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Notes internes rattachables à une entité (client, entreprise, commande, projet, réservation, professionnel).
          Les notes <span className="font-semibold text-foreground-700">privées ne sont jamais exposées à l’AI Client</span>.
        </p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle note
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-sticky-note-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Notes au total</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-lock-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.privateCount}</p>
          <p className="text-xs text-foreground-500">Privées (hors AI)</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-pushpin-2-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pinned}</p>
          <p className="text-xs text-foreground-500">Épinglées</p>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        <button onClick={() => setEntityFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${entityFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Toutes</button>
        {NOTE_ENTITY_TYPES.map((e) => (
          <button key={e.key} onClick={() => setEntityFilter(e.key)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${entityFilter === e.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{e.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-sticky-note-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune note pour l’instant.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((n) => {
            const vis = noteVisibilityMeta(n.visibility);
            const ent = noteEntityMeta(n.entity_type);
            return (
              <div key={n.id} className="flex flex-col bg-background-50 border border-background-200/70 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                    {n.title && <span className="text-sm font-semibold text-foreground-900 truncate">{n.title}</span>}
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${vis.className}`}><i className={vis.icon}></i>{vis.label}</span>
                  </div>
                  <button onClick={() => pin(n)} disabled={busyId === n.id} title="Épingler" className={`w-7 h-7 flex items-center justify-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${n.pinned ? 'text-primary-600' : 'text-foreground-300 hover:bg-background-100'}`}><i className="ri-pushpin-2-line"></i></button>
                </div>
                <p className="text-xs text-foreground-600 mt-2 whitespace-pre-wrap line-clamp-4 flex-1">{n.body}</p>
                <div className="flex items-center justify-between gap-2 mt-3">
                  <span className="text-[11px] text-foreground-400 truncate">
                    {ent ? `${ent.label}${n.entity_id ? ` #${n.entity_id}` : ''}` : 'Générale'}
                    {n.author_name ? ` · ${n.author_name}` : ''}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(n)} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                    <button onClick={() => remove(n)} disabled={busyId === n.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier la note' : 'Nouvelle note'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.body.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité liée</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
                  <option value="">— Générale —</option>
                  {NOTE_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
                <input className={INPUT} value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} placeholder="Ex : 100234" disabled={!form.entityType} />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Titre (optionnel)</span>
              <input className={INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Préférence client" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Contenu</span>
              <textarea rows={4} className={`${INPUT} resize-none`} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={500} placeholder="Votre note…" />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Visibilité</span>
              <div className="flex flex-wrap gap-2">
                {NOTE_VISIBILITIES.map((v) => (
                  <button key={v.key} type="button" onClick={() => setForm({ ...form, visibility: v.key })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors cursor-pointer ${form.visibility === v.key ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-600 hover:bg-background-100'}`}>
                    <i className={v.icon}></i>{v.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-foreground-400 mt-1.5">{noteVisibilityMeta(form.visibility).desc}</p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}